<script lang="ts">
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';

	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	import { capFirstChar } from '../../../../helpers/utilities';
	import {
		onSettingsResponse,
		HTTPS_CERTIFICATE_DOCS_URL,
	} from '../../../../helpers/device_settings_helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { rebootRequired } from 'src/stores/device-runtime/states';

	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';

	//Design system components
	import { Alert, Button, Modal, Text, Container } from '@Shelly/shelly-design-system';

	//Components
	import TlsCertificateUploadModal from './TlsCertificateUploadModal.svelte';

	export let device;

	const id = real_id(device.id);

	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;
	$: enhancedSecurity = settings?.sys?.device?.enhanced_security;

	let showEnableModal = false;
	let showCertModal = false;
	let loading = false;

	const handleEnable = async () => {
		loading = true;

		const data = { config: { device: { enhanced_security: true } } };
		const res: SettingRequest = await gen2Request(device, 'sys.setconfig', data);

		const response = onSettingsResponse(res, device, {
			success: $format('enhanced_security_enabled_scc_toast', {
				default: 'Enhanced security enabled successfully.',
			}),
		});

		if (response?.isok) {
			const updatedSettings = {
				...settings,
				sys: {
					...settings.sys,
					device: {
						...settings.sys.device,
						enhanced_security: true,
					},
				},
			};
			devices_settings.update_devices_settings(id, updatedSettings);

			setSidePanelState({ showSettingsModal: false });

			if (!response?.restart_required && !response?.data?.restart_required) {
				rebootRequired.set(device);
			}
		}

		loading = false;
	};
</script>

{#if enhancedSecurity === true}
	<Alert
		type="info"
		heading={$format('enhanced_security_enabled_heading', {
			default: 'Enhanced Security is active',
		})}
		message={$format('enhanced_security_enabled_description', {
			default:
				'HTTPS, strict TLS certificate validation, and HTTP-to-HTTPS redirect are enforced.',
		})}
		cta={{
			title: capFirstChar(
				$format('enhanced_security_manage_certificates', {
					default: 'Manage certificates',
				}),
			),
		}}
		on:cta_click={() => (showCertModal = true)}
	/>
{:else if enhancedSecurity === false}
	<Alert
		type="info"
		heading={$format('enhanced_security_banner_heading', {
			default: 'Enhance your device security',
		})}
		message={$format('enhanced_security_banner_description', {
			default:
				'Enhance your device security by enforcing strict certificate validation and enabling HTTPS redirection.',
		})}
		cta={{
			title: capFirstChar(
				$format('button_enable', {
					default: 'Enable',
				}),
			),
		}}
		on:cta_click={() => (showEnableModal = true)}
	/>
{/if}

<!-- Enable Enhanced Security Modal -->
{#if showEnableModal}
	<Modal
		showModal={true}
		on:close={() => (showEnableModal = false)}
		title={$format('enhanced_security_modal_title', { default: 'Enable Advanced Security' })}
	>
		<svelte:fragment slot="content">
			<Container padding="0">
				<Text styleType="body" class="mb-m">
					<strong>
						{$format('enhanced_security_modal_intro', {
							default: 'What changes when you enable it:',
						})}
					</strong>
				</Text>

				<ul class="enhanced-security-list mb-m">
					<li class="mb-m">
						<Text styleType="body-small">
							<strong>
								{$format('enhanced_security_modal_cert_title', {
									default: 'Stronger certificate validation',
								})}
							</strong>
							{' — '}
							{$format('enhanced_security_modal_cert_body', {
								default:
									'Your device will no longer accept connections from servers with expired or invalid certificates. This protects your automations and integrations from potential interception.',
							})}
						</Text>
					</li>
					<li>
						<Text styleType="body-small">
							<strong>
								{$format('enhanced_security_modal_https_title', {
									default: 'HTTPS access to your device',
								})}
							</strong>
							{' — '}
							{$format('enhanced_security_modal_https_body', {
								default:
									'Enables encrypted browser connections when an HTTPS Server Certificate is uploaded. Without a certificate, the device continues to work normally over HTTP.',
							})}
						</Text>
					</li>
				</ul>

				<Text styleType="body-small" class="mb-m">
					{$format('enhanced_security_modal_docs', {
						default:
							'For a step-by-step guide on obtaining and uploading an HTTPS certificate, see',
					})}
					{' '}
					<Text
						element="a"
						href={HTTPS_CERTIFICATE_DOCS_URL}
						target="_blank"
						rel="noopener noreferrer"
						class="link-underline d-inline"
						styleType="body-small"
					>
						{$format('enhanced_security_modal_docs_link', {
							default: 'API Documentation: HTTPS Certificate Setup',
						})}
					</Text>.
				</Text>

				<Alert
					type="warning"
					heading={$format('enhanced_security_modal_warning', {
						default:
							'Once enabled, this feature cannot be disabled without performing a factory reset.',
					})}
				/>
			</Container>
		</svelte:fragment>

		<div slot="footer" class="d-flex justify-content-end flex-wrap gap-s">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				variant="secondary"
				on:click={() => (showEnableModal = false)}
			>
				{capFirstChar($format('button_cancel'))}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				variant="primary"
				{loading}
				on:click={() => {
					showEnableModal = false;
					handleEnable();
				}}
			>
				{capFirstChar($format('button_enable', { default: 'Enable' }))}
			</Button>
		</div>
	</Modal>
{/if}

<!-- Certificate Upload Modal -->
<TlsCertificateUploadModal {device} bind:showModal={showCertModal} />

<style>
	.enhanced-security-list {
		list-style: disc;
		padding-left: 1.25rem;
		margin: 0;
	}
</style>
